import { useState } from "react";

import { formatBytes } from "./composerAttachments";
import styles from "./MessageAttachments.module.css";
import { ThreadMedia } from "./ThreadMedia";
import { attachmentContentPath, previewKind } from "../data/api/attachments";
import type { MessageAttachment } from "../data/types";
import { Icon } from "../icons";

/** Add or drop one id, without mutating the set the render is holding. */
const toggled = (set: ReadonlySet<string>, id: string): ReadonlySet<string> => {
  const next = new Set(set);
  if (!next.delete(id)) {
    next.add(id);
  }
  return next;
};

/**
 * The files a message carried, under its body (chat.md §Attachments;
 * side-threads.md §Attachments — a side thread message carries the same
 * shape). Shared by the primary thread's transcript (`ChatTab.tsx`) and a
 * side thread's (`SideThreadPanel.tsx`) — one row per file, whatever state
 * it's in.
 *
 * One row per file, in one of three states. A picture or a clip previews
 * inline through the authenticated content route and pops out on a click; a
 * preview the reader has put away is a chip that opens back up; everything
 * else is a chip that downloads.
 *
 * Collapsing to the chip rather than to nothing is what keeps the strip
 * honest: a file is on this message whatever the reader has chosen to look at,
 * and the collapsed state is a shape the surface already had for the files
 * that were never previewable. Nothing new had to be invented to hold it —
 * where a disclosure over the whole strip would have meant a summary row on
 * every message that carries so much as one screenshot.
 *
 * The `unplayable` set is the one state here that is not the reader's. A
 * `.mov` announces a container and not a codec, so whether the browser can
 * decode it is not knowable until it tries — and when it cannot, the file is a
 * download and not a collapsed preview, so it gets the chip that says so and
 * no way to open it back up.
 */
export const MessageAttachments = ({
  attachments,
}: {
  readonly attachments: ReadonlyArray<MessageAttachment>;
}) => {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [unplayable, setUnplayable] = useState<ReadonlySet<string>>(new Set());
  return (
    <div className={styles.msgAttachments}>
      {attachments.map((a) => {
        const kind = unplayable.has(a.publicId) ? null : previewKind(a.mime);
        const collapse = (): void => {
          setCollapsed((prev) => toggled(prev, a.publicId));
        };
        if (kind && !collapsed.has(a.publicId)) {
          return (
            <ThreadMedia
              alt={a.name}
              bound="message"
              bytes={a.bytes}
              filename={a.name}
              key={a.publicId}
              kind={kind}
              name={a.name}
              onCollapse={collapse}
              onUnplayable={() => {
                setUnplayable((prev) => new Set(prev).add(a.publicId));
              }}
              src={attachmentContentPath(a.publicId)}
            />
          );
        }
        const body = (
          <>
            <span className={styles.msgAttachmentName}>{a.name}</span>
            <span className={styles.msgAttachmentSize}>
              {formatBytes(a.bytes)}
            </span>
          </>
        );
        // A chevron rather than the file icon, pointing the way the tool
        // card's does when there is something behind it. The two chips are
        // deliberately alike otherwise: they are the same row about the same
        // file, and only the click differs.
        return kind ? (
          <button
            className={styles.msgAttachmentFile}
            key={a.publicId}
            onClick={collapse}
            title={`Show ${a.name}`}
            type="button"
          >
            <Icon.ChevDown size={13} style={{ transform: "rotate(-90deg)" }} />
            {body}
          </button>
        ) : (
          <a
            className={styles.msgAttachmentFile}
            download={a.name}
            href={attachmentContentPath(a.publicId)}
            key={a.publicId}
          >
            <Icon.Doc size={13} />
            {body}
          </a>
        );
      })}
    </div>
  );
};
